/* Explicit choices for product options selected by the central Liquid policy. */
(() => {
  'use strict';
  if (window.HLManualSelection) return;
  const selector = 'variant-selects[data-hl-manual-selection="true"]';
  const prompt = '';
  const picker = (info) => info?.querySelector(selector);
  const groups = (node) => Array.from(node?.querySelectorAll('[data-hl-option-group]') || []);
  const selected = (group) => group.querySelector('input[type="radio"]:checked') ||
    group.querySelector('select')?.selectedOptions[0];
  const choices = (info) => groups(picker(info)).map((group) => {
    const input = selected(group);
    return input?.dataset.optionValueId ? { id: input.dataset.optionValueId, value: input.value } : null;
  });
  const complete = (info) => {
    const values = choices(info);
    return values.length > 0 && values.every(Boolean);
  };
  const updateErrors = (info) => {
    const firstMissing = info.hlManualAttempted ?
      groups(picker(info)).find((group) => !selected(group)?.dataset.optionValueId) : null;
    groups(picker(info)).forEach((group) => {
      const error = group.querySelector('[data-hl-option-error]');
      if (error) error.hidden = group !== firstMissing;
      if (group === firstMissing && error) {
        group.setAttribute('aria-describedby', error.id);
        group.setAttribute('aria-invalid', 'true');
      } else {
        group.removeAttribute('aria-describedby');
        group.removeAttribute('aria-invalid');
      }
      const select = group.querySelector('select');
      if (select) {
        if (group === firstMissing && error) {
          select.setAttribute('aria-describedby', error.id);
          select.setAttribute('aria-invalid', 'true');
        } else {
          select.removeAttribute('aria-describedby');
          select.removeAttribute('aria-invalid');
        }
      }
    });
    return firstMissing;
  };
  const showMissing = (info) => {
    info.hlManualAttempted = true;
    const group = updateErrors(info);
    if (!group) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    group.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    group.querySelector('input, select')?.focus({ preventScroll: true });
  };
  const setStatus = (info, text, retry = false) => {
    const status = picker(info)?.querySelector('[data-hl-selection-status]');
    if (status) { status.textContent = text; status.hidden = !text; }
    const button = picker(info)?.querySelector('[data-hl-selection-retry]');
    if (button) button.hidden = !retry;
  };
  const ids = (info) => info.querySelectorAll(
    'product-form input[name="id"], form[id^="product-form-installment-"] input[name="id"]'
  );
  const payments = (info, hidden) => info.querySelectorAll('[data-hl-manual-payment]').forEach((node) => {
    node.hidden = hidden;
  });
  const lock = (info, text = prompt, retry = false) => {
    info.dataset.hlManualReady = 'false';
    picker(info)?.removeAttribute('data-hl-resolved');
    ids(info).forEach((input) => { input.value = ''; });
    const button = info.productForm?.submitButton || info.querySelector('product-form [name="add"]');
    if (button) {
      button.disabled = text === 'Checking your selection…' || retry;
      const label = button.querySelector('span');
      if (label) label.textContent = text === 'Checking your selection…' ? text : window.variantStrings.addToCart;
    }
    payments(info, true);
    setStatus(info, text, retry);
    updateErrors(info);
  };
  const reset = (info) => {
    const node = picker(info);
    if (!node) return;
    info.hlManualAttempted = false;
    groups(node).forEach((group) => {
      if (group.dataset.hlOptionManual === 'false') return;
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = false;
        input.removeAttribute('checked');
      });
      const select = group.querySelector('select');
      if (select) {
        Array.from(select.options).forEach((option) => {
          option.selected = !option.dataset.optionValueId;
          if (!option.dataset.optionValueId) option.setAttribute('selected', 'selected');
          else option.removeAttribute('selected');
        });
      }
      const label = group.querySelector('[data-selected-value]');
      if (label) label.textContent = '';
    });
    lock(info);
    info.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const init = (info) => {
    if (!picker(info) || info.hlManualInitialized) return;
    info.hlManualInitialized = true;
    reset(info);
  };
  const optionChange = (info, event) => {
    if (!picker(info)) return true;
    if (!picker(info).contains(event.target)) return false;
    init(info);
    const colorChange = event.target.closest('[data-hl-option-group]')?.dataset.hlOptionManual === 'false';
    info.hlManualColorPreview = colorChange && !complete(info);
    if (!complete(info) && !info.hlManualColorPreview) {
      info.abortController?.abort();
      info.pendingRequestUrl = null;
      lock(info);
      return false;
    }
    lock(info, 'Checking your selection…');
    return true;
  };
  const previewAutomaticOption = (info, html, variant, productUrl) => {
    if (!picker(info) || !info.hlManualColorPreview || complete(info)) return false;
    const values = choices(info);
    const matches = variant && Array.isArray(variant.options) && variant.options.length === values.length &&
      values.every((choice, index) => !choice || choice.value === variant.options[index]);
    info.hlManualColorPreview = false;
    info.updateOptionValues(html);
    if (matches) {
      info.updateMedia(html, variant.featured_media?.id);
      const source = html.getElementById(`price-${info.sectionId}`);
      const destination = info.querySelector(`#price-${info.dataset.section}`);
      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('hidden');
      }
      info.updateURL(productUrl, variant.id);
    }
    lock(info);
    return true;
  };
  const prepareOptions = (info, newPicker) => {
    if (!picker(info) || newPicker.dataset.hlManualSelection !== 'true') return;
    const values = choices(info);
    groups(newPicker).forEach((group, index) => {
      const chosen = values[index];
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const checked = !!chosen && input.dataset.optionValueId === chosen.id;
        input.checked = checked;
        input.toggleAttribute('checked', checked);
      });
      const select = group.querySelector('select');
      if (select) Array.from(select.options).forEach((option) => {
        const checked = chosen ? option.dataset.optionValueId === chosen.id : !option.dataset.optionValueId;
        option.selected = checked;
        if (checked) option.setAttribute('selected', 'selected');
        else option.removeAttribute('selected');
      });
      const label = group.querySelector('[data-selected-value]');
      if (label && !select) label.textContent = chosen?.value || '';
    });
  };
  const filterVariant = (info, variant) => {
    if (!picker(info)) return variant;
    const values = choices(info);
    return variant?.id && complete(info) && Array.isArray(variant.options) &&
      variant.options.length === values.length &&
      values.every((choice, index) => choice.value === variant.options[index]) ? variant : null;
  };
  const resolved = (info, variant, html) => {
    if (!picker(info)) return;
    variant = filterVariant(info, variant);
    const sourceButton = html.getElementById(`ProductSubmitButton-${info.sectionId}`);
    const canBuy = !!variant?.available && sourceButton?.dataset.hlVariantPurchasable === 'true';
    const button = info.productForm?.submitButton || info.querySelector('product-form [name="add"]');
    info.dataset.hlManualReady = canBuy ? 'true' : 'false';
    picker(info).setAttribute('data-hl-resolved', variant ? 'true' : 'false');
    if (!canBuy) {
      ids(info).forEach((input) => { input.value = ''; });
      if (button) {
        button.disabled = true;
        const label = button.querySelector('span');
        if (label) label.textContent = variant ? window.variantStrings.soldOut : window.variantStrings.unavailable;
      }
      payments(info, true);
      setStatus(info, variant ? 'This selection is sold out or cannot be purchased in the required quantity.' :
        'This combination is unavailable. Please choose another option.');
      return;
    }
    if (button) {
      button.disabled = false;
      const label = button.querySelector('span');
      if (label) label.textContent = window.variantStrings.addToCart;
    }
    payments(info, false);
    window.Shopify?.PaymentButton?.init?.();
    setStatus(info, '');
    updateErrors(info);
  };
  const failed = (info) => {
    if (picker(info)) lock(info, 'Your selection could not be checked. Please try again.', complete(info));
  };
  const guardPurchase = (event) => {
    const target = event.target;
    const info = target.closest?.('product-info');
    if (!picker(info)) return;
    const retry = target.closest?.('[data-hl-selection-retry]');
    if (retry && event.type === 'click') {
      event.preventDefault();
      const input = groups(picker(info)).map(selected).filter(Boolean).at(-1);
      const select = input?.closest('select');
      (select || input)?.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const purchase = event.type === 'submit' ? target.matches('form[data-type="add-to-cart-form"]') :
      target.closest?.('product-form [name="add"], [data-hl-manual-payment]');
    if (!purchase || info.dataset.hlManualReady === 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showMissing(info);
  };
  window.HLManualSelection = { init, isRequired: (info) => !!picker(info), optionChange, prepareOptions,
    filterVariant, resolved, failed, previewAutomaticOption };
  document.addEventListener('click', guardPurchase, true);
  document.addEventListener('submit', guardPurchase, true);
  document.addEventListener('product-info:loaded', (event) => init(event.target));
  const initializePage = () => document.querySelectorAll('product-info').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializePage);
  else initializePage();
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) document.querySelectorAll('product-info').forEach(reset);
  });
})();
